import React, { useState, useEffect } from 'react';
import { FiX, FiUsers } from 'react-icons/fi';
import { getUsers } from '../../../api/users';
import { reassignTask } from '../../../api/retailPerformanceTasks';
import { toast } from 'react-toastify';

const ReassignTaskModal = ({ isOpen, onClose, onSuccess, taskId, currentAssignedUsers = [] }) => {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      // Initialize with currently assigned users
      if (currentAssignedUsers && currentAssignedUsers.length > 0) {
        setSelectedUserIds(currentAssignedUsers.map(u => u.id));
      }
    }
  }, [isOpen, currentAssignedUsers]);

  const fetchUsers = async () => {
    try {
      // Fetch all staff users
      const response = await getUsers({ role: ['staff', 'branch_staff', 'branch_admin'], limit: 1000 });
      if (response.success) {
        setUsers(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedUserIds.length === 0) {
        toast.error('Please select at least one user');
        return;
    }

    setLoading(true);

    try {
      const response = await reassignTask(taskId, selectedUserIds);
      if (response.success) {
        toast.success('Task reassigned successfully');
        onSuccess();
        onClose();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reassign task');
    } finally {
      setLoading(false);
    }
  };

  const toggleUser = (userId) => {
    setSelectedUserIds(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex justify-between items-center mb-5">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-white flex items-center gap-2">
                <FiUsers /> Manage Assignees
              </h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <FiX className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Select Users to Assign *</label>
                <div className="border border-gray-300 rounded-md max-h-60 overflow-y-auto p-2 dark:bg-gray-700 dark:border-gray-600">
                  {users.map(user => (
                    <div key={user.id} className="flex items-center py-2 px-2 hover:bg-gray-50 dark:hover:bg-gray-600 rounded">
                      <input
                        id={`reassign-user-${user.id}`}
                        type="checkbox"
                        className="h-4 w-4 text-accent-gold focus:ring-accent-gold border-gray-300 rounded"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => toggleUser(user.id)}
                      />
                      <label htmlFor={`reassign-user-${user.id}`} className="ml-3 block text-sm text-gray-900 dark:text-white cursor-pointer flex-1">
                        {user.full_name} <span className="text-gray-500 text-xs">({user.role})</span>
                      </label>
                    </div>
                  ))}
                  {users.length === 0 && <p className="text-sm text-gray-500 p-2">No staff users found.</p>}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Selected: {selectedUserIds.length} users
                </p>
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="submit"
                  disabled={loading || selectedUserIds.length === 0}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-accent-gold text-base font-medium text-white hover:bg-accent-goldDark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold sm:col-start-2 sm:text-sm disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Update Assignments'}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-gold sm:mt-0 sm:col-start-1 sm:text-sm dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReassignTaskModal;
